import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {IValidationErrorObject} from '../../../incase-domain/common/validationErrorResponse';
import Button from '../../../incase-domain/components/button/Button';
import DropdownMenuCompact from '../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Label from '../../../incase-domain/components/label/Label';
import {BUTTON_TYPE, IElementButton} from '../../../incase-domain/elements/button';
import {buttonValidator} from '../../../incase-domain/validators/elements/button';
import {BUTTON_MAX} from '../../../incase-domain/validators/elements/constant';
import {getErrorClasses} from '../../../utils/validation';
import SectionTitle from '../../elements/common/SectionTitle';
import HighlightedInputMedia from '../../fileLoader/input.media';
import useImgFileReader from '../../fileLoader/util/useImgFileReader';
import {TABS} from '../form/part/tabs';
import AnimationForm from './common/animationForm';
import DebouncedTextInput from './common/debounced/DebouncedTextInput';
import DebouncedNumberInput from './common/debounced/DebounceNumberInput';
import ElementShadow from './common/elementShadow';
import FontAndBackgroundColorPicker, {COLOR_FIELDS} from './common/fontAndBackgroundColorPicker';
import FontSelect from './common/fontSelect';
import FormElementOptions from './common/formOptions/formElementOptions';
import SlideChange from './common/slideChange';
import {useHighlightedElement, useSetHighlightedElement} from '../../../utils/hooks/slideUpdates/useSetSlideElement';

interface HighlightedButtonElementComponentProps {
    errors: IValidationErrorObject | null;
    overrideHighlightedElement?: IElementButton;
    overrideHighlightedElementChangeHandler?: {single: (field: keyof IElementButton | string, value: unknown) => void};
    inForm?: boolean;
    activeTab: string;
}

function HighlightedButtonElementComponent(props: HighlightedButtonElementComponentProps): ReactElement {
    const {errors, overrideHighlightedElement, overrideHighlightedElementChangeHandler, inForm = false, activeTab} = props;
    const {t} = useTranslation();
    const updateHighlightedElement = useSetHighlightedElement();
    const highlightedElement = useHighlightedElement() as IElementButton;
    const element = overrideHighlightedElement || highlightedElement;
    const {icon} = element;
    const changeHandler = overrideHighlightedElementChangeHandler || updateHighlightedElement;

    const onFileLoad = useImgFileReader((images) => {
        const {imageSrc} = images[0];
        const icon = {src: imageSrc, width: 2, height: 2};
        changeHandler.single('icon', icon);
    });

    if (activeTab === TABS.STYLE.id) {
        return <ElementShadow highlightedElement={element} highlightedElementChangeHandler={changeHandler} />;
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <AnimationForm
                    appearAfter={element.appearAfter || 0}
                    elementAnimations={element.elementAnimations}
                    highlightedElementChangeHandler={changeHandler}
                />
                <div className="l-group--form">
                    <div className="l-group__item">
                        <SlideChange
                            slideChange={element.slideChange}
                            setSlideChange={(slideChange) => {
                                changeHandler.single('slideChange', slideChange);
                            }}
                        />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.content')} />
                <div className={`l-group__item ${getErrorClasses(errors, 'text')}`}>
                    <Label>{t('designSidepanel.buttonText')}</Label>
                    <DebouncedTextInput
                        value={element.text}
                        onChange={(value) => changeHandler.single('text', value)}
                        placeholder={t('designSidepanel.buttonText')}
                        className="c-sidepanel__input"
                        maxLength={BUTTON_MAX}
                        validator={(val) => {
                            const validation = buttonValidator.shape.text.safeParse(val);
                            return validation.success;
                        }}
                        trailing={
                            <HighlightedInputMedia
                                type="image"
                                onLoad={onFileLoad}
                                child={
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        color="primary"
                                        square
                                        leading={icon ? 'solar:gallery-minimalistic-bold' : 'solar:gallery-linear'}
                                    />
                                }
                            />
                        }
                    />
                </div>

                {icon && (
                    <>
                        <div className="l-group__item">
                            <DebouncedNumberInput
                                label={t('designSidepanel.iconWidth')}
                                value={icon.width || 1}
                                onChange={(newValue: number) =>
                                    changeHandler.single('icon', {
                                        ...icon,
                                        width: newValue,
                                    })
                                }
                                step={0.1}
                                min={0.1}
                            />
                        </div>

                        <div className="l-group__item">
                            <DebouncedNumberInput
                                label={t('designSidepanel.iconHeight')}
                                value={icon.height || 1}
                                onChange={(newValue: number) =>
                                    changeHandler.single('icon', {
                                        ...icon,
                                        height: newValue,
                                    })
                                }
                                step={0.1}
                                min={0.1}
                            />
                        </div>
                    </>
                )}
            </div>

            {inForm && (
                <>
                    <div className="l-group--form">
                        <div className="l-group__item">
                            <Label>{t('designSidepanel.htmlType')}</Label>
                            <DropdownMenuCompact
                                width="full"
                                items={[
                                    BUTTON_TYPE.map((el) => ({
                                        label: el,
                                        onClick: () => changeHandler.single('htmlType', el),
                                    })),
                                ]}
                            >
                                <Button
                                    className="c-sidepanel__dropdown_button"
                                    variant="soft"
                                    trailing="solar:alt-arrow-down-linear"
                                    size="xs"
                                    fullWidth
                                >
                                    {element.htmlType || t('designSidepanel.chooseHtmlType')}
                                </Button>
                            </DropdownMenuCompact>
                        </div>
                    </div>

                    <FormElementOptions highlightedElement={element} highlightedElementChangeHandler={changeHandler} />
                </>
            )}

            <>
                <div className="l-group--form">
                    <SectionTitle label={t('designSidepanel.style')} />
                    <div className="u-flex u-flex-col u-gap-2">
                        <DebouncedNumberInput
                            label={t('designSidepanel.fontSize')}
                            value={element.font.size || 1}
                            onChange={(newValue: number) => changeHandler.single('font', {...element.font, size: newValue})}
                            step={0.1}
                            min={0.1}
                        />
                        <FontSelect highlightedElement={element} highlightedElementChangeHandler={changeHandler} />
                        <FontAndBackgroundColorPicker
                            highlightedElement={element}
                            highlightedElementChangeHandler={changeHandler}
                            colorFields={[COLOR_FIELDS.BACKGROUND_COLOR, COLOR_FIELDS.FONT_COLOR]}
                        />
                    </div>
                </div>
            </>
        </>
    );
}

export default HighlightedButtonElementComponent;
